import { CaretRight, Close, Search } from "@carbon/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react";
import { FixedSizeList as VirtualList, ListChildComponentProps } from "react-window";
import { useRecoilState, useRecoilValue } from "recoil";
import { brandList, dropdownList, selectedBrands, selPOICategories } from "../../states";
import { Typography } from "../Shadcn/Typography";
import { Button } from "../Shadcn/Button";
import { Checkbox } from "../Shadcn/Checkbox";
import { Input } from "../Shadcn/Input";
import { getMarkerColor } from "./markerUtils";

interface WatchListProps {
    isOpen: boolean;
    handleListOpen: (keyName: string, tagName?: string, buttonEl?: HTMLElement | null) => void;
    brandCheckBoxRef: React.RefObject<HTMLDivElement>;
    brandButtonRef: React.RefObject<HTMLDivElement>;
    style: React.CSSProperties;
    layer?: any;
}

export const BrandWatchList: React.FC<WatchListProps> = ({ isOpen, handleListOpen, brandCheckBoxRef, brandButtonRef, style, layer }) => {
    const selCategoryList = useRecoilValue(selPOICategories);
    const geoGropdownList = useRecoilValue(dropdownList);
    const brandsData = useRecoilValue(brandList);
    const brandsKey = layer?.markertype === "poi" ? "poimarker" : (layer?.type ?? "poimarker")
    const brands = useMemo(() => brandsData?.[brandsKey] ?? [], [brandsData, brandsKey]);
    const [filteredBrands, setFilteredBrands] = useState(brands);
    const [selBrands, setSelBrands] = useRecoilState(selectedBrands);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selTempBrands, setSelTempBrands] = useState<any[]>(selBrands || []);

    const selTempSet = useMemo(
        () => new Set((selTempBrands || []).map((b: any) => b.companyName)),
        [selTempBrands]
    );

    const filteredByName = useMemo(() => {
        const map = new Map<string, any>();
        filteredBrands.forEach(b => map.set(b.companyName, b));
        return map;
    }, [filteredBrands]);

    const handleSelectAll = () => {
        setSelTempBrands((prev: any) => {
            const prevSet = new Set(prev.map((b: any) => b.companyName));
            const isAllFilteredSelected = filteredBrands.every(c => prevSet.has(c.companyName));
            if (isAllFilteredSelected) {
                return prev.filter((item: any) => !filteredByName.has(item.companyName));
            }
            const toAdd = filteredBrands.filter(fb => !prevSet.has(fb.companyName));
            return [...prev, ...toAdd];
        });
    };

    const handleChange = useCallback((companyName: string, checked: boolean) => {
        setSelTempBrands((prev: any[]) => {
            if (checked) {
                const item = filteredByName.get(companyName);
                return item ? [...prev, item] : prev;
            }
            return prev.filter(item => item.companyName !== companyName);
        });
    }, [filteredByName]);

    const handleCancelFilter = () => {
        setSelTempBrands(selBrands);
        handleListOpen('brands');
    }

    const handleApplyFilter = () => {
        setSelBrands(selTempBrands);
        handleListOpen('brands');
    }

    useEffect(() => {
        setFilteredBrands(brands || []);
        setSelTempBrands(prev => prev.filter((item: any) => (brands || []).some((b: any) => b.companyName === item.companyName)));
    }, [brands]);

    useEffect(() => {
        if (!searchTerm) return setFilteredBrands(brands);
        return setFilteredBrands(brands?.filter((item) =>
            item.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [searchTerm]);

    useEffect(() => {
        if (!isOpen) setSelTempBrands([...selBrands]);
    }, [isOpen]);

    const selectedFilteredCount = useMemo(
        () => filteredBrands.reduce((acc, fb) => acc + (selTempSet.has(fb.companyName) ? 1 : 0), 0),
        [filteredBrands, selTempSet]
    );
    const allCheckState: boolean | 'indeterminate' =
        selectedFilteredCount === 0
            ? false
            : selectedFilteredCount === filteredBrands.length
                ? true
                : 'indeterminate';

    const layerTitle = layer?.title ?? layer?.name ?? 'Markers';

    const ROW_HEIGHT = 36;
    const MAX_LIST_HEIGHT = Math.round(window.innerHeight * 0.45);
    const listHeight = Math.min(MAX_LIST_HEIGHT, Math.max(ROW_HEIGHT, filteredBrands.length * ROW_HEIGHT));

    const Row = useCallback(({ index, style: rowStyle }: ListChildComponentProps) => {
        const category = filteredBrands[index];
        if (!category) return null;
        const checked = selTempSet.has(category.companyName);
        return (
            <div
                style={rowStyle}
                className="flex items-center gap-2 cursor-pointer px-4 poi-category"
            >
                <Checkbox
                    id={`brand-${category.companyName}`}
                    checked={checked}
                    onCheckedChange={(val) => handleChange(category.companyName, !!val)}
                    className="shrink-0"
                />
                <Typography
                    variant="b2"
                    className="flex-1 min-w-0 truncate leading-[19.2px] tracking-[var(--letter-spacing-default)] text-[var(--general-text-primary)]"
                    title={`${category.companyName} (${category.branchCount})`}
                >
                    {`${category.companyName} (${category.branchCount})`}
                </Typography>
            </div>
        );
    }, [filteredBrands, selTempSet, handleChange]);

    return (
        <div ref={brandCheckBoxRef} className="relative w-full">
            {/* trigger row */}
            <div
                ref={brandButtonRef}
                className="flex items-center justify-between cursor-pointer py-1"
                onClick={(event) => { handleListOpen('brands', (event.target as HTMLElement).tagName, event.currentTarget as HTMLElement); setSearchTerm(''); }}
            >
                <Typography variant="b3" className="text-[var(--ds-solid-neutral-11)] truncate flex-1 min-w-0">Highlight</Typography>
                <div className="flex items-center gap-1.5">
                    {(() => {
                        const [r, g, b] = getMarkerColor(layer?.type || 'poimarker')
                        return (
                            <span
                                className="shrink-0 rounded-full inline-block h-2 w-2"
                                style={{
                                    backgroundColor: `rgb(${r},${g},${b})`,
                                    border: `1px solid rgb(${Math.round(r * 0.6)},${Math.round(g * 0.6)},${Math.round(b * 0.6)})`,
                                }}
                            />
                        )
                    })()}
                    <CaretRight size={12} className="icon-gray-0 p-0 shrink-0" />
                </div>
            </div>

            {isOpen && (
                <div className='poi-category-list-home' style={style} onMouseDown={e => e.stopPropagation()}>
                    {/* header */}
                    <div className="flex items-center justify-between border-b px-4 py-2.5">
                        <Typography
                            variant="b2"
                            className="font-medium truncate pr-2"
                            title={`Highlight Markers: ${layerTitle}`}
                        >
                            Highlight Markers: {layerTitle}
                        </Typography>
                        <button className="cursor-pointer text-foreground" onClick={handleCancelFilter}>
                            <Close size={14} />
                        </button>
                    </div>

                    {/* search */}
                    <div className="px-1 py-2">
                        <Input
                            size="small"
                            placeholder="Search by keyword"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            leftIcon={<Search size={16} />}
                            className="w-full border-t-0 border-x-0 border-b border-border rounded-none shadow-none hover:border-border focus:border-border focus:ring-0"
                        />
                    </div>

                    {/* list */}
                    <div className="py-1">
                        {/* select all row */}
                        <div className="flex items-center justify-between cursor-pointer px-4 py-2 poi-category">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="brand-select-all"
                                    checked={allCheckState}
                                    onCheckedChange={handleSelectAll}
                                />
                                <Typography
                                    variant="b2"
                                    className="font-medium leading-[19.2px] tracking-[var(--letter-spacing-default)] text-[var(--general-text-primary)]"
                                >
                                    {`All (${filteredBrands.length})`}
                                </Typography>
                            </div>
                        </div>

                        <VirtualList
                            height={listHeight}
                            itemCount={filteredBrands.length}
                            itemSize={ROW_HEIGHT}
                            width="100%"
                            itemKey={(index) => filteredBrands[index]?.companyName ?? index}
                            className="no-scrollbar"
                        >
                            {Row}
                        </VirtualList>
                    </div>

                    {/* footer */}
                    <div className="flex items-center justify-end gap-2 border-t px-4 py-2.5">
                        <Button variant="outline" size="small" onClick={handleCancelFilter}>
                            Cancel
                        </Button>
                        <Button variant="primary" size="small" onClick={handleApplyFilter}>
                            Apply
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
